import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { sendAppointmentMeetLink } from '../api';
import { useCurrentUserProfile } from '../../hook/useCurrentUserProfile';
import {
  getAppointmentScheduleFields,
  getBrowserTimeZone,
  readAnyContactName,
  timeToMinutes,
} from '../utils';

export const RECONNECT_GOOGLE_MESSAGE =
  'Reconnect Google Workspace to add a Meet link.';

export function readMeetLink(source) {
  const link = source?.google_meet_link;
  return typeof link === 'string' && link.trim() ? link.trim() : '';
}

export function readMeetError(source) {
  const message = source?.google_sync_error;
  return typeof message === 'string' && message.trim() ? message.trim() : '';
}

export function JoinMeetButton({ href, className = '' }) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className={`inline-flex items-center justify-center rounded-md bg-[#50AB3D] px-2 py-0.5 text-[11px] font-semibold text-white hover:bg-[#459636] ${className}`}
    >
      Join
    </a>
  );
}

export function MeetDetail({
  appointment,
  status,
  flush = false,
  companyId,
  contactPhone,
}) {
  const { companyName } = useCurrentUserProfile();
  if (status === 'cancelled') return null;
  const link = readMeetLink(appointment);
  const error = readMeetError(appointment);
  if (!link && !error) return null;
  const canSend =
    !!link &&
    status === 'scheduled' &&
    !!String(contactPhone || '').trim() &&
    !!companyId &&
    !!appointment?.id;

  const displayLink = link ? link.replace(/^https?:\/\//, '') : '';
  const inviteText = link
    ? buildMeetInviteText({ appointment, link, companyName })
    : '';

  return (
    <div className={`flex flex-col gap-1.5 ${flush ? '' : 'pl-6'}`}>
      {link ? (
        <div className="flex items-start gap-3.5 p-3.5 bg-white rounded-[12px] border border-[#EBEBEB] hover:shadow-sm transition-all group min-w-0">
          <div className="flex-shrink-0 mt-0.5">
            <GoogleMeetIcon />
          </div>

          <div className="flex flex-col items-start min-w-0 w-full">
            <div className="flex items-center justify-between w-full gap-2">
              <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#1A73E8] font-semibold text-[15px] hover:underline whitespace-nowrap"
              >
                Join with Google Meet
              </a>
              <div className="flex items-center gap-1">
                <CopyMeetLink text={inviteText} />
              </div>
            </div>
            <span className="text-[13px] text-[#5F6368] truncate w-full mt-0.5">
              {displayLink}
            </span>
            <span className="text-[12px] text-[#5F6368] mt-0.5">
              Up to 100 guest connections
            </span>
            {canSend ? (
              <SendMeetLinkButton
                companyId={companyId}
                appointmentId={appointment.id}
              />
            ) : null}
          </div>
        </div>
      ) : null}
      {error ? <MeetSyncWarning message={error} /> : null}
    </div>
  );
}

function SendMeetLinkButton({ companyId, appointmentId }) {
  const mutation = useMutation({
    mutationFn: () => sendAppointmentMeetLink({ companyId, appointmentId }),
    onSuccess: (data) => {
      const channel = data?.channel === 'template' ? 'template' : 'session';
      toast.success(`Meet link sent (${channel})`);
    },
    onError: (err) => {
      const message = err?.response?.data?.error;
      toast.error(
        typeof message === 'string' && message.trim()
          ? message
          : 'Failed to send Meet link',
      );
    },
  });

  return (
    <button
      type="button"
      disabled={mutation.isPending}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!mutation.isPending) mutation.mutate();
      }}
      className="mt-2 h-8 px-3 rounded-lg bg-[#50AB3D] text-white text-xs font-semibold hover:bg-[#459636] disabled:opacity-60"
    >
      {mutation.isPending ? 'Sending...' : 'Send Meet link'}
    </button>
  );
}

function GoogleMeetIcon() {
  return (
    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#EBF4EA] shrink-0">
      <svg
        className="w-[22px] h-[22px]"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <defs>
          <linearGradient
            id="meetDetailBody"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="100%"
          >
            <stop offset="0%" stopColor="#FFD33D" />
            <stop offset="100%" stopColor="#FF9900" />
          </linearGradient>
          <linearGradient
            id="meetDetailLens"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="100%"
          >
            <stop offset="0%" stopColor="#FFAB2B" />
            <stop offset="100%" stopColor="#F57C00" />
          </linearGradient>
          <radialGradient id="meetDetailGlow" cx="100%" cy="50%" r="60%">
            <stop offset="0%" stopColor="#FF88AA" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#FF88AA" stopOpacity="0" />
          </radialGradient>
        </defs>
        <path
          d="M14 9.5L20 6C21.1 5.4 22.5 6.2 22.5 7.5V16.5C22.5 17.8 21.1 18.6 20 18L14 14.5V9.5Z"
          fill="url(#meetDetailLens)"
        />
        <path
          d="M14 9.5L20 6C21.1 5.4 22.5 6.2 22.5 7.5V16.5C22.5 17.8 21.1 18.6 20 18L14 14.5V9.5Z"
          fill="url(#meetDetailGlow)"
        />
        <rect
          x="2.5"
          y="5.5"
          width="13"
          height="13"
          rx="4"
          fill="url(#meetDetailBody)"
        />
        <rect
          x="2.5"
          y="5.5"
          width="13"
          height="13"
          rx="4"
          fill="url(#meetDetailGlow)"
        />
        <circle cx="6.5" cy="15" r="2" fill="white" />
      </svg>
    </div>
  );
}

function meetClock(hhmm) {
  const mins = timeToMinutes(hhmm);
  if (mins == null) return null;
  let hour = Math.floor(mins / 60) % 24;
  const minute = mins % 60;
  const suffix = hour >= 12 ? 'pm' : 'am';
  hour = hour % 12 || 12;
  return { hm: `${hour}:${String(minute).padStart(2, '0')}`, suffix };
}

function formatMeetRange(start, end) {
  const from = meetClock(start);
  const to = meetClock(end);
  if (!from || !to) return '';
  if (from.suffix === to.suffix) return `${from.hm} – ${to.hm}${to.suffix}`;
  return `${from.hm}${from.suffix} – ${to.hm}${to.suffix}`;
}

function formatMeetDate(dayKey) {
  if (!dayKey) return '';
  const [year, month, day] = dayKey.split('-').map(Number);
  if (!year || !month || !day) return dayKey;
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(new Date(year, month - 1, day));
}

export function buildMeetInviteText({ appointment, link, companyName }) {
  const schedule = getAppointmentScheduleFields(appointment);
  const contact =
    readAnyContactName(appointment?.contact) ||
    readAnyContactName(appointment) ||
    appointment?.contact_name ||
    'Appointment';
  const company = companyName || 'Company';
  const dateLabel = formatMeetDate(schedule.date);
  const timeLabel = formatMeetRange(schedule.start_time, schedule.end_time);
  const timezone = schedule.timezone || getBrowserTimeZone();
  return [
    `${contact} <> ${company}`,
    [dateLabel, timeLabel].filter(Boolean).join(' · '),
    `Time zone: ${timezone}`,
    'Google Meet joining info',
    `Video call link: ${link}`,
  ].join('\n');
}

function CopyMeetLink({ text }) {
  const [copied, setCopied] = useState(false);

  const copy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      title={copied ? 'Copied!' : 'Copy link'}
      className="p-1.5 rounded-full hover:bg-[#F1F3F4] text-[#5F6368] transition-colors"
    >
      {copied ? (
        <svg
          className="w-4 h-4 text-[#50AB3D]"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth="2.5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5 13l4 4L19 7"
          />
        </svg>
      ) : (
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth="2"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
          />
        </svg>
      )}
    </button>
  );
}

export function MeetSyncWarning({ message }) {
  if (!message) return null;
  const reconnect = message === RECONNECT_GOOGLE_MESSAGE;
  return (
    <p className="text-xs font-medium text-[#F1A132]">
      {message}
      {reconnect ? (
        <>
          {' '}
          <Link
            to="/integrations/google-workspace"
            className="underline underline-offset-2"
          >
            Open Google Workspace
          </Link>
        </>
      ) : null}
    </p>
  );
}

export function MeetSaveNotice({ appointment }) {
  const link = readMeetLink(appointment);
  const error = readMeetError(appointment);
  if (!link && !error) return null;
  return (
    <div className="px-4 pb-2">
      <MeetDetail
        appointment={appointment}
        status={appointment?.status}
        flush
      />
    </div>
  );
}
